import {AxiosResponse} from 'axios'
import {HttpInstance} from '../createHttpInstance'
import {
  FACE_OFF_RUNS_URL,
  decodeRun,
  isFiniteNumber,
  isRecord,
} from './getFaceOffRuns'
import {
  FaceOffBox,
  FaceOffChange,
  FaceOffChangeKind,
  FaceOffFace,
  FaceOffFlip,
  FaceOffRunDetail,
  FaceOffRunFace,
  FaceOffRunFaceChange,
  FaceOffRunInfo,
  FaceOffVerdict,
  FaceOffVerdictValue,
} from './types'

export const faceOffRunUrl = (runId: string): string =>
  `${FACE_OFF_RUNS_URL}/${encodeURIComponent(runId)}`

/** One run's diff: GET /v2/faces/faceoff/runs/{runId}. Untrusted until decoded. */
export const getFaceOffRun = (
  http: HttpInstance,
  runId: string,
): Promise<AxiosResponse<unknown>> =>
  http.request<unknown>({method: 'GET', url: faceOffRunUrl(runId)})

const fail = (what: string): never => {
  throw new Error(`Malformed Face Off run detail: ${what}`)
}

const isNullableNumber = (value: unknown): value is number | null =>
  value === null || isFiniteNumber(value)

const CHANGE_KINDS: string[] = ['regression', 'improvement', 'shift']
const VERDICT_VALUES: string[] = ['accept', 'reject', 'unsure']

export const decodeBox = (raw: unknown, what: string): FaceOffBox | null => {
  if (raw === null) return null
  if (
    !isRecord(raw) ||
    !isFiniteNumber(raw.left) ||
    !isFiniteNumber(raw.top) ||
    !isFiniteNumber(raw.width) ||
    !isFiniteNumber(raw.height)
  ) {
    return fail(`${what}.bbox`)
  }
  return {left: raw.left, top: raw.top, width: raw.width, height: raw.height}
}

const decodeFace = (raw: unknown, what: string): FaceOffFace | null => {
  if (raw === null) return null
  if (
    !isRecord(raw) ||
    typeof raw.path !== 'string' ||
    !isFiniteNumber(raw.photoId) ||
    !isFiniteNumber(raw.personId) ||
    !isFiniteNumber(raw.clientId) ||
    !('bbox' in raw) ||
    !isNullableNumber(raw.score) ||
    !isFiniteNumber(raw.rank) ||
    !isFiniteNumber(raw.threshold) ||
    typeof raw.accepted !== 'boolean' ||
    (raw.albumId !== undefined && !isNullableNumber(raw.albumId)) ||
    (raw.photoUrl !== undefined && typeof raw.photoUrl !== 'string') ||
    (raw.candidateUrl !== undefined && typeof raw.candidateUrl !== 'string')
  ) {
    return fail(what)
  }
  const face: FaceOffFace = {
    path: raw.path,
    photoId: raw.photoId,
    personId: raw.personId,
    clientId: raw.clientId,
    bbox: decodeBox(raw.bbox, what),
    score: raw.score,
    rank: raw.rank,
    threshold: raw.threshold,
    accepted: raw.accepted,
  }
  if (raw.albumId !== undefined) face.albumId = raw.albumId
  if (raw.photoUrl) face.photoUrl = raw.photoUrl
  if (raw.candidateUrl) face.candidateUrl = raw.candidateUrl
  return face
}

export const decodeVerdict = (
  raw: unknown,
  what: string,
): FaceOffVerdict | null => {
  if (raw === null) return null
  if (
    !isRecord(raw) ||
    typeof raw.path !== 'string' ||
    !isFiniteNumber(raw.photoId) ||
    !isFiniteNumber(raw.personId) ||
    !('bbox' in raw) ||
    typeof raw.verdict !== 'string' ||
    !VERDICT_VALUES.includes(raw.verdict) ||
    typeof raw.note !== 'string' ||
    typeof raw.by !== 'string' ||
    typeof raw.utc !== 'string'
  ) {
    return fail(`${what}.verdict`)
  }
  return {
    path: raw.path,
    photoId: raw.photoId,
    personId: raw.personId,
    bbox: decodeBox(raw.bbox, `${what}.verdict`),
    verdict: raw.verdict as FaceOffVerdictValue,
    note: raw.note,
    by: raw.by,
    utc: raw.utc,
  }
}

const decodeChange = (raw: unknown, what: string): FaceOffChange => {
  if (
    !isRecord(raw) ||
    typeof raw.kind !== 'string' ||
    !CHANGE_KINDS.includes(raw.kind) ||
    !('before' in raw) ||
    !('after' in raw) ||
    !isNullableNumber(raw.scoreDelta)
  ) {
    return fail(what)
  }
  return {
    kind: raw.kind as FaceOffChangeKind,
    before: decodeFace(raw.before, `${what}.before`),
    after: decodeFace(raw.after, `${what}.after`),
    scoreDelta: raw.scoreDelta,
  }
}

const decodeFlip = (raw: unknown, index: number): FaceOffFlip => {
  const what = `flips[${index}]`
  const change = decodeChange(raw, what)
  if (!isRecord(raw) || !('verdict' in raw)) return fail(`${what}.verdict`)
  return {...change, verdict: decodeVerdict(raw.verdict, what)}
}

const RUN_FACE_CHANGES: string[] = ['', 'flip', 'shift']

const decodeRunFace = (raw: unknown, index: number): FaceOffRunFace => {
  const what = `faces[${index}]`
  const face = decodeFace(raw, what)
  if (
    face === null ||
    !isRecord(raw) ||
    !('previous' in raw) ||
    typeof raw.change !== 'string' ||
    !RUN_FACE_CHANGES.includes(raw.change)
  ) {
    return fail(what)
  }
  return {
    ...face,
    previous: decodeFace(raw.previous, `${what}.previous`),
    change: raw.change as FaceOffRunFaceChange,
  }
}

const decodeLinks = <T>(
  raw: unknown,
  what: string,
  isValue: (value: unknown) => value is T,
): Record<string, T> => {
  if (raw === undefined) return {}
  if (!isRecord(raw)) return fail(what)
  const out: Record<string, T> = {}
  Object.entries(raw).forEach(([key, value]) => {
    if (!isValue(value)) fail(`${what}.${key}`)
    out[key] = value as T
  })
  return out
}

const isString = (value: unknown): value is string => typeof value === 'string'
export const isStringList = (value: unknown): value is string[] =>
  Array.isArray(value) && value.every(isString)

const decodeRunInfo = (raw: unknown): FaceOffRunInfo => {
  const summary = decodeRun(raw, 0)
  if (
    !isRecord(raw) ||
    !isFiniteNumber(raw.band) ||
    !isFiniteNumber(raw.passed) ||
    !isFiniteNumber(raw.failed) ||
    !isFiniteNumber(raw.total) ||
    !isFiniteNumber(raw.passRate)
  ) {
    return fail('run')
  }
  return {
    ...summary,
    band: raw.band,
    passed: raw.passed,
    failed: raw.failed,
    total: raw.total,
    passRate: raw.passRate,
  }
}

/**
 * Decodes media's nested run detail. One missing or mistyped required field
 * rejects the whole response.
 */
export const decodeFaceOffRun = (body: unknown): FaceOffRunDetail => {
  if (
    !isRecord(body) ||
    !(body.previous === null || typeof body.previous === 'string') ||
    typeof body.rebaseline !== 'boolean' ||
    !isNullableNumber(body.baselineFrom) ||
    !Array.isArray(body.flips) ||
    !Array.isArray(body.shifts) ||
    (body.faces !== undefined && !Array.isArray(body.faces))
  ) {
    return fail('body')
  }
  return {
    run: decodeRunInfo(body.run),
    previous: body.previous,
    rebaseline: body.rebaseline,
    baselineFrom: body.baselineFrom,
    flips: body.flips.map(decodeFlip),
    shifts: body.shifts.map((raw, i) => decodeChange(raw, `shifts[${i}]`)),
    faces: Array.isArray(body.faces) ? body.faces.map(decodeRunFace) : [],
    photoUrls: decodeLinks(body.photoUrls, 'photoUrls', isString),
    refUrls: decodeLinks(body.refUrls, 'refUrls', isStringList),
  }
}
